import { useState } from 'react';
import startcase from 'lodash.startcase';
import styled from 'styled-components';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import useIsMounted from '../../../custom-hooks/useIsMounted';
import { device } from '../../../../utils/styled-component-mixins';
import EventWebinarFormattedTime from './EventWebinarFormattedTime';
import { useLocalization } from '../../../custom-hooks/useLocalization';
import useMediaQuery from '../../../custom-hooks/useMediaQuery';

const EventWebinarCard = (props) => {
  const { resourceStrings } = useLocalization();
  const isDesktop = useMediaQuery(device.desktop);
  const [hoverState, setHoverState] = useState(undefined);
  const isMounted = useIsMounted();
  const featuredText = resourceStrings['bdO.EventsAndWebinarsSliderWidget.FeaturedText'];
  const {
    city,
    state,
    dateFrom,
    description,
    pageTitle,
    slug,
    timeZone,
    isFeatured,
    cardIsExternal,
    cardExternalLink
  } = props;
  const eventTypes = {
    0: resourceStrings['bdO.EventsWebinars.Event'],
    1: resourceStrings['bdO.EventsWebinars.Webinar'],
    2: resourceStrings['bdO.EventsWebinars.PastWebinar'],
    default: resourceStrings['bdO.EventsWebinars.Event']
  };
  const eventType = eventTypes[props.eventType] || eventTypes.default;
  const formatTime = (isoDate) =>
    DateTime.fromISO(isoDate, { setZone: true, zone: timeZone }).toFormat('t');

  const ctaText =
    props.eventType !== 2
      ? resourceStrings['bdO.EventsWebinarsSliderWidget.CardCtaText']
      : resourceStrings['bdO.EventsWebinars.ViewRecording'];
  const ctaAriaLabel = props.eventType !== 2 ? `Register for ${pageTitle}` : `View ${pageTitle}`;

  return (
    <div className={props.className}>
      <div
        className="event-webinar-card-wrapper"
        onMouseEnter={() => setHoverState('hover-in')}
        onMouseLeave={() => setHoverState('hover-out')}
      >
        {isFeatured && <div className="featured-tag">{props.featuredText ?? featuredText}</div>}
        <a
          href={cardIsExternal ? cardExternalLink : slug}
          className="non-default-link"
          aria-label={ctaAriaLabel}
          target={cardIsExternal ? '_blank' : 'self'}
        >
          <div className="featured-card-content">
            <div
              className={classNames('animation-wrapper', hoverState, {
                mounted: isMounted
              })}
            >
              <div
                className={classNames('event-column-wrapper', {
                  'search-card': props.isSearchResultsEventCard
                })}
              >
                <div className="event-left-column">
                  {(!props.isSearchResultsEventCard || isDesktop) && (
                    <time className="event-date" dateTime={dateFrom}>
                      <EventWebinarFormattedTime isoDate={dateFrom} timeZone={timeZone} />
                    </time>
                  )}
                </div>
                <div className="event-right-column">
                  <div className="right-column-content">
                    <span className="tag">{startcase(eventType)}</span>
                    {!props.isSearchResultsEventCard || isDesktop ? (
                      <>
                        <div className="event-time-location">
                          <time dateTime={formatTime(dateFrom)} suppressHydrationWarning>
                            {formatTime(dateFrom)} {timeZone}
                          </time>
                          {city && state && (
                            <address>
                              <span>
                                {city}, {state}
                              </span>
                            </address>
                          )}
                        </div>
                        <h3>{pageTitle}</h3>
                        <p>{description}</p>
                      </>
                    ) : (
                      <>
                        <div className="event-time-location">
                          <h3>{pageTitle}</h3>
                          <div className="time-location-wrapper">
                            <time dateTime={formatTime(dateFrom)} suppressHydrationWarning>
                              <div className="search-page-date">
                                <EventWebinarFormattedTime
                                  isMonthFirst={true}
                                  fullMonth={true}
                                  isoDate={dateFrom}
                                  timeZone={timeZone}
                                />
                              </div>
                              {formatTime(dateFrom)} {timeZone}
                            </time>
                            {city && state && (
                              <>
                                <span> | </span>
                                <address>
                                  <span>
                                    {city}, {state}
                                  </span>
                                </address>
                              </>
                            )}
                          </div>
                        </div>
                        <p>{description}</p>
                      </>
                    )}
                  </div>
                  <div className="overflow-wrapper">
                    <div className="cta-wrapper">
                      <p className="cta-line">
                        <span>{ctaText}</span>
                        <span
                          className={
                            cardIsExternal
                              ? 'material-icons-round external-insight-icon'
                              : 'material-icons-round'
                          }
                          aria-hidden="true"
                        >
                          {cardIsExternal ? 'open_in_new' : 'chevron_right'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

export default styled(EventWebinarCard)`
  display: flex;
  height: 100%;
  position: relative;

  .featured-card-content {
    background-color: var(--white);
    height: 100%;
    padding: 4.8rem 1.6rem 3.1rem 0.7rem;
    position: relative;
    overflow: hidden;
    z-index: 100;
    color: var(--charcoal);

    @media ${device.desktop} {
      padding: 4.8rem 4rem 3.1rem 0.7rem;
    }
  }

  .animated-content {
    position: relative;
    width: calc(100% - 4.8rem);
    z-index: 100;

    @media ${device.desktop} {
      margin: 2.4rem 3.2rem 3.2rem 3.2rem;
      width: calc(100% - 6.4rem);
    }
  }

  .event-column-wrapper {
    column-gap: 1.5rem;
    display: flex;
    height: 100%;
    position: relative;
    z-index: 100;

    &.search-card {
      column-gap: 0;

      .time-location-wrapper {
        address {
          display: inline;
        }

        address,
        time {
          color: var(--charcoal-2);
        }

        time {
          display: inline;
        }
      }
    }
  }

  .event-right-column {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .right-column-content {
      margin-top: -0.4rem;
    }

    h3 {
      font-size: 1.6rem;
      font-weight: 700;
      line-height: 2.4rem;
      margin: 0 0 1.6rem 0;

      @media ${device.desktop} {
        font-size: 2.2rem;
        font-weight: 800;
        line-height: 3.2rem;
        margin: 0 0 0.8rem 0;
      }
    }

    p {
      display: none;

      @media ${device.desktop} {
        display: block;
        font-size: 1.4rem;
      }
    }

    time {
      display: block;
    }
  }

  .overflow-wrapper {
    overflow: hidden;
    position: relative;
    z-index: 100;
  }

  .tag {
    display: inline-block;
    margin-bottom: 1.6rem;
  }

  .cta-line {
    display: flex !important;
    align-items: center;
  }

  .event-date {
    display: block;
    margin-top: -0.9rem;
    width: 6.6rem;

    .event-day {
      font-size: 3rem;
      line-height: 4rem;
    }

    > span {
      color: var(--charcoal-2);
      display: block;
      font-weight: 800;
      text-align: center;
    }

    span:not(.event-day) {
      font-size: 1.4rem;
      line-height: 2rem;
    }
  }

  .event-time-location {
    margin-bottom: 1.6rem;
  }

  address,
  time {
    font-size: 1.4rem;
    font-style: normal;
    line-height: 2.4rem;
  }

  .featured-tag {
    z-index: 101;
  }

  .non-default-link {
    text-decoration: none;
  }

  .featured-card-content {
    .publish-date {
      display: block;
      color: var(--charcoal-2);
      font-size: 1.2rem;
      letter-spacing: 0.07rem;
      line-height: 1.6rem;
      margin-bottom: 3rem;
      text-transform: uppercase;
    }
  }

  .animation-wrapper {
    height: 100%;

    @media ${device.desktop} {
      padding-top: calc(16rem - 3.2rem);
      overflow: hidden;
      padding-top: 0;

      &:after {
        background-color: var(--grey);
        content: '';
        height: 110%;
        position: absolute;
        right: 200%;
        top: 0;
        transform: skewX(50deg);
        transition: all 0.3s ease-in;
        width: 375%;
        z-index: 1;
      }

      &.hover-in.mounted {
        animation: translate-right 0.3s forwards;

        &::after {
          animation: translate-right 0.3s forwards;
          background-color: var(--slate);
        }
        * {
          color: var(--white);
        }
        .cta-wrapper {
          transform: translateX(0);
        }

        .animated-content {
          height: 100%;
          top: 0;
        }
        .featured-card-wrapper {
          .content-wrapper {
            bottom: 0;

            p {
              opacity: 1;
            }
          }
        }

        .tag {
          background-color: var(--slate);
          border-color: var(--charcoal);
        }
      }

      .cta-wrapper {
        transform: translateX(calc(-100% + 24px));
      }

      &.hover-out.mounted {
        animation: translate-left 0.3s forwards;

        &::after {
          animation: translate-left 0.3s forwards;
        }

        .featured-card-wrapper {
          .content-wrapper {
            bottom: var(--bottom-position);

            p {
              transition-delay: 0;
              transition: all 0.2s ease-out;
            }
          }
        }

        .tag {
          background-color: var(--white);
        }
      }
    }
  }

  .overflow-wrapper > .cta-wrapper {
    align-items: center;
    color: var(--cta-blue);
    display: flex;
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.8rem;
    margin-top: 1rem;
    text-decoration: none;

    .external-insight-icon {
      position: relative;
      left: 3.5px;
      font-size: 2.6rem !important;
    }

    span:last-child {
      font-size: 3.2rem;
    }
    @media ${device.tablet} {
      transition: all 0.3s ease-in-out;

      span:first-child {
        transition: all 0.3s ease-in-out;
      }
    }
  }

  @keyframes translate-right {
    from {
      right: 210%;
    }

    to {
      right: -150%;
    }
  }

  @keyframes translate-left {
    from {
      right: -110%;
    }

    to {
      right: 210%;
    }
  }
`;
